import React, { useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';

import {
  trackClickDoneButton,
  trackClickMainBackButton,
  trackClickTakeMeBack,
} from '../../../analytics';

import { FancyBtn } from '../../../components/custom';
import { Button, Modal } from '../../../components/general';

import { URL } from '../../../constants';

import useSampleProjectData from '../../../hooks/useSampleProjectData';

import { useProjectStore } from '../../../stores/project';

import { getUrl, getVBLinkParts, getPluralPhrase } from '../../../utils';

interface Props {
  subheading?: string;
  isBack?: boolean;
  addClass?: string;
}

const DoneBtn: React.FC<Props> = (props) => {
  const navigate = useNavigate();

  const { subheading, isBack, addClass } = props;

  const { currentProject } = useProjectStore();

  const [uploadData, setUploadData] = useState(
    JSON.parse(localStorage.getItem('uploadData') ?? '{}'),
  );

  const [showAnalyseModal, setShowAnalyseModal] = useState(false);

  const { eventId, page } = getVBLinkParts(window.location.pathname);
  const vbLink = getUrl(URL.VIDEO_BUILDER, eventId);

  // sample project data
  const { isSample } = useSampleProjectData(eventId);

  const { url, text, showModal } = useMemo(() => {
    const isAttendeeReview =
      currentProject?.attendeeReviewData?.isAttendeeReview ?? false;
    const isAssigneeAccess = currentProject?.isAssigneeAccess;
    let url = isAssigneeAccess ? URL.VB_ASSIGNEE : URL.VIDEO_BUILDER;
    let text = 'Done';
    let showModal = false;

    if (!isBack && !isAssigneeAccess && uploadData.eventId === eventId) {
      if (!uploadData.peopleCount && !isAttendeeReview) {
        url = URL.VB_PEOPLE;
        text = 'Next Step: Upload People';
      } else if (!uploadData.isHlReelUploaded) {
        url = URL.VB_HL_REEL;
        text = 'Next Step: Upload Highlight Reel';
      } else if (!uploadData.clipsCount) {
        url = URL.VB_CLIPS;
        text = 'Next Step: Upload Clips';
      }

      // show modal – if is not attendee review, it is in upload stage, all assets uploaded, and user didn't reject analysis before
      else if (
        !isAttendeeReview &&
        !isAssigneeAccess &&
        currentProject?.stage === 'upload' &&
        uploadData.peopleCount &&
        uploadData.isHlReelUploaded &&
        uploadData.clipsCount &&
        !uploadData.rejectedAnalysis
      )
        showModal = true;
    }

    return { url, text, showModal };
  }, [currentProject, eventId, isBack, uploadData]);

  useEffect(() => {
    const handler = () =>
      setUploadData(JSON.parse(localStorage.getItem('uploadData') ?? '{}'));

    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  useEffect(() => {
    if (showModal) setShowAnalyseModal(true);
  }, [showModal]);

  const handleBackClick = () => {
    navigate(vbLink);

    trackClickMainBackButton(page, isSample);
  };

  const handleNextClick = () => {
    navigate(getUrl(url, eventId));

    trackClickDoneButton(page, text, isSample);
  };

  const onClickAnalyse = () => {
    navigate(`${vbLink}?startAnalysis=true`);
  };

  const onClickTakeMeBack = () => {
    navigate(vbLink);

    localStorage.setItem(
      'uploadData',
      JSON.stringify({ ...uploadData, rejectedAnalysis: true }),
    );

    trackClickTakeMeBack(page, isSample);
  };

  if (isBack)
    return (
      <Button
        onClick={handleBackClick}
        variant="custom"
        className={classNames(
          'flex flex-col items-center justify-center w-full min-h-[var(--form-el-height)] h-auto p-4 gap-2 rounded-md',
          addClass,
        )}
      >
        <div className="font-bold">Back</div>

        {subheading && <div className="text-sm">{subheading}</div>}
      </Button>
    );

  return (
    <React.Fragment>
      <Button
        onClick={handleNextClick}
        className={classNames('w-full font-bold', addClass)}
      >
        {text}
      </Button>

      <Modal show={showAnalyseModal} onHide={() => setShowAnalyseModal(false)}>
        <Modal.Body centered>
          <h4>Ready to analyse uploaded assets?</h4>

          <div>
            Analyse <b>{getPluralPhrase(uploadData.clipsCount, 'clip')}</b> for{' '}
            <b>{getPluralPhrase(uploadData.peopleCount, 'person')}</b>. Our AI
            will look for the best clips to add to your Highlight Reels wildcard
            shots.
          </div>

          <FancyBtn text="Analyse" onClick={onClickAnalyse} />

          {isSample && (
            <Button onClick={onClickTakeMeBack} variant="link" colour="black">
              Not yet, take me back
            </Button>
          )}
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default DoneBtn;
